
import { makeAutoObservable, runInAction } from 'mobx'
import { uniqBy } from 'lodash'
import { injectable, inject, decorate } from 'inversify'

// evertel
import { DocumentTileData } from '@evertel/types'
import { Api } from '@evertel/api'
import { DocumentTileStore } from '../../store'

/** a search result is the document's compiled room tile, keyed by the document it came from */
interface DocumentSearchByRoomResult extends DocumentTileData {
    id: number
}

/**
 * Searches the documents shared with a room, returned as their compiled room tiles. Results
 * are put in the DocumentTileStore too, so each DocumentTile finds its tile there rather than
 * fetching it
 */
class DocumentSearchByRoomController {
    documents: DocumentSearchByRoomResult[] = []
    documentsLatestFetch: DocumentSearchByRoomResult[] = []
    documentsCount = 0
    roomId = 0
    searchTerm = ''
    page = 1
    limit = 30
    isLoading = false
    isInfiniteScroll = true

    constructor(
        private api: Api,
        private documentTileStore: DocumentTileStore
    ) {
        makeAutoObservable(this)
    }

    init(roomId: number, isInfiniteScroll = true) {
        this.roomId = roomId
        this.isInfiniteScroll = isInfiniteScroll
        this.resetSearch()
    }

    setSearchTerm(value: string) {
        this.resetSearchMeta()
        this.searchTerm = value
    }

    setPage(pageNumber: number) {
        this.page = pageNumber
    }

    search = async () => {
        if (!this.roomId) return

        runInAction(() => {
            this.isLoading = true
        })

        // need to grab these here because of async issues that can happen after the first await
        const localFilter = this.filter
        const localSearchTerm = this.searchTerm
        const localRoomId = this.roomId


        let count = this.documentsCount
        let results: DocumentSearchByRoomResult[] = []

        this.documentsLatestFetch = []

        try {
        // grab the total count of docs in this room
            if (!this.documentsCount) {
                count = await this.fetchDocumentsCount(localFilter.where)
            }

            // stop from fetching if we already got them all
            if (count > this.documents?.length) {
                const tiles = await this.api.Routes.Room.getDocumentsSearchTiles(localRoomId, localFilter) as unknown as DocumentTileData[]
                results = tiles.map(tile => ({ ...tile, id: tile.documentId }))
            }

            // Check if parameters have changed before updating state
            if ( this.searchTerm !== localSearchTerm || this.roomId !== localRoomId ) {
                // Parameters have changed; discard stale results
                return
            }

            runInAction(() => {
                this.documentsLatestFetch = results
                this.documents = (this.isInfiniteScroll) ? uniqBy([...this.documents, ...results], 'id') : results

                this.documentTileStore.update(results)
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    async fetchDocumentsCount(where = this.filter.where) {
        const res = await this.api.Routes.Room.getDocumentsSearchCount(this.roomId, where)

        runInAction(() => {
            this.documentsCount = res?.count ?? 0
        })

        return this.documentsCount
    }

    fetchNextDocuments() {
        this.page = this.page + 1
        this.search()
    }

    resetSearchMeta() {
        this.documents = []
        this.documentsCount = 0
        this.page = 1
    }

    resetSearch() {
        this.resetSearchMeta()
        this.searchTerm = ''
    }

    get filter() {
        return {
            where: {
                searchTerm: this.searchTerm
            },
            order: 'updatedDate DESC',
            limit: this.limit,
            skip: (this.page - 1) * this.limit
        }
    }

}

decorate(injectable(), DocumentSearchByRoomController)
decorate(inject(Api), DocumentSearchByRoomController, 0)
decorate(inject(DocumentTileStore), DocumentSearchByRoomController, 1)

export { DocumentSearchByRoomController, DocumentSearchByRoomResult }
